import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Button, CircularProgress, useMediaQuery } from "@mui/material";
import { gql, useApolloClient } from "@apollo/client";
import { useUserContext } from "../../hooks/userContext";
import { useNotification } from "../../contexts/NotificationContext";
import { usePresence } from "../../contexts/PresenceContext";
import { useHomeownerOnboardingAccess } from "../../hooks/useHomeownerOnboardingAccess";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import {
  MatchProfileDetail,
  MatchReaction,
  fetchPresenceStatuses,
  fetchMatchProfile,
  fetchMatches,
  postMatchReaction,
} from "../../services/matchesApi";
import { fetchMatchSummary, selectPerfectMatch } from "../../services/collaborationApi";
import { Pages } from "../../utils";
import {
  MATCH_REACTION_EVENT,
  broadcastMatchReactionUpdate,
  getMatchReactionStorageKey,
  readMatchReactionOverrides,
  type MatchReactionOverrideMap,
} from "../../utils/match-reaction-cache";
import {
  FILTER_QUERY_MAP,
  PAGE_SIZE,
  SORT_OPTIONS,
  SORT_STORAGE_KEY,
} from "./constants";
import type { DesignerBioImages, FilterKey, MatchRecord, SortKey } from "./types";
import {
  applyReactionOverrides,
  buildPaginationItems,
  getDesignerBioLookupId,
  getLocationLabel,
  getMatchCandidateIds,
  getNextReaction,
  getProfileId,
  getProfileRoute,
  getTopTags,
  hasLikeReaction,
  hasLoveReaction,
  resolvePresenceStatusForMatch,
  shouldShowSelectPerfectMatch,
  trackMatchesEvent,
} from "./utils";
import { MatchDetailPanel } from "./components/MatchDetailPanel";
import { MatchesListPanel } from "./components/MatchesListPanel";
import { MatchesMobileCarousel } from "./components/MatchesMobileCarousel";
import { MatchesPagination } from "./components/MatchesPagination";
import { MatchesToolbar } from "./components/MatchesToolbar";
import { MatchMobileDetail } from "./components/MatchMobileDetail";
import DashboardHeader from "../../components/dashboard-header";
import "./styles.scss";

const GET_DESIGNER_BIO_IMAGES = gql`
  query GetDesignerBioImages($designerId: String!) {
    getDesignerBio(designerId: $designerId) {
      image {
        url
      }
      coverImage {
        url
      }
      designerId {
        _id
      }
    }
  }
`;

export const MatchesPage = () => {
  const { user } = useUserContext();
  const {
    effectiveRole,
    canAccessAdminFeatures,
  } = useEffectiveRole();
  const { matchingProfileCompleted } = useHomeownerOnboardingAccess();
  const { getPresenceStatus } = usePresence();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const apolloClient = useApolloClient();

  const [matches, setMatches] = useState<MatchRecord[]>([]);
  const [selectingMatchId, setSelectingMatchId] = useState<string | null>(null);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sortOption, setSortOption] = useState<SortKey>(() => {
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY);
      if (stored && SORT_OPTIONS.includes(stored as SortKey)) {
        return stored as SortKey;
      }
    } catch {
      // Ignore storage failures
    }
    return "best_match_rate";
  });
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalMatches, setTotalMatches] = useState(0);
  const [profileCache, setProfileCache] = useState<
    Record<string, MatchProfileDetail>
  >({});
  const [loadingProfileId, setLoadingProfileId] = useState<string | null>(null);
  const [bioImageCache, setBioImageCache] = useState<
    Record<string, DesignerBioImages>
  >({});
  const [hasPreviousMatches, setHasPreviousMatches] = useState(false);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [activeDesignerId, setActiveDesignerId] = useState<string | null>(null);
  const [allMatchesCache, setAllMatchesCache] = useState<MatchRecord[]>([]);
  const hasLoadedRef = useRef(false);
  const hasLoadedAllMatchesRef = useRef(false);
  const loadingAllMatchesRef = useRef(false);
  const requestedBioImageIdsRef = useRef<Set<string>>(new Set());
  const mobileCarouselRef = useRef<HTMLDivElement | null>(null);
  const viewerAccountType =
    effectiveRole === "Homeowner" || effectiveRole === "Designer"
      ? effectiveRole
      : user?.accountType ?? null;
  const isAdminHomeownerPreview =
    canAccessAdminFeatures &&
    effectiveRole === "Homeowner" &&
    (user?.accountType ?? "").toLowerCase() !== "homeowner";
  const matchFetchOptions = useMemo(
    () =>
      isAdminHomeownerPreview
        ? ({ previewRole: "homeowner" } as const)
        : undefined,
    [isAdminHomeownerPreview]
  );
  const isHomeowner = (viewerAccountType ?? "").toLowerCase() === "homeowner";
  const shouldCompleteMatchingProfile =
    isHomeowner && !isAdminHomeownerPreview && !matchingProfileCompleted;
  const matchPresenceRole = isHomeowner ? "Designer" : "Homeowner";
  const hasAppliedQueryFilter = useRef(false);
  const lockedToActiveMatch = useMemo(
    () => isHomeowner && Boolean(activeDesignerId),
    [activeDesignerId, isHomeowner]
  );
  const allMatchesCacheRef = useRef<MatchRecord[]>([]);

  useEffect(() => {
    allMatchesCacheRef.current = allMatchesCache;
  }, [allMatchesCache]);

  useEffect(() => {
    if (hasAppliedQueryFilter.current) return;
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter");
    if (filterParam) {
      const mapped = FILTER_QUERY_MAP[filterParam.toLowerCase()];
      if (mapped) {
        setFilter(mapped);
      }
    }
    hasAppliedQueryFilter.current = true;
  }, [location.search]);

  useEffect(() => {
    if (!user?.id) return;
    if (!isHomeowner || isAdminHomeownerPreview) {
      setActiveDesignerId(null);
      return;
    }
    let cancelled = false;
    fetchMatchSummary(user.id)
      .then((summary) => {
        if (cancelled) return;
        setActiveDesignerId(summary.activeCollaboration?.designerId ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setActiveDesignerId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminHomeownerPreview, isHomeowner, user?.id]);

  useEffect(() => {
    hasLoadedRef.current = false;
    hasLoadedAllMatchesRef.current = false;
    loadingAllMatchesRef.current = false;
    setMatches([]);
    setSelectedMatchId(null);
    setBioImageCache({});
    setAllMatchesCache([]);
    requestedBioImageIdsRef.current.clear();
  }, [isAdminHomeownerPreview, user?.id]);

  const loadMatches = useCallback(
    async (pageToLoad = 1, append = false) => {
      if (!user?.id) return;
      try {
        if (!append && !hasLoadedRef.current) {
          setLoading(true);
        }
        setError(null);
        const data = await fetchMatches(user.id, pageToLoad, matchFetchOptions);
        const incoming = data.matches;
        const pageMatches = append ? incoming : incoming.slice(0, PAGE_SIZE);
        let stored: string | null = null;
        try {
          stored = localStorage.getItem("hdg_matches_ids");
        } catch {
          stored = null;
        }
        setHasPreviousMatches(Boolean(stored && stored.length));
        setMatches((prev) => {
          const reactionMap = new Map(prev.map((match) => [match.matchId, match.reaction]));
          const combined = append ? [...prev, ...pageMatches] : pageMatches;
          const seen = new Set<string>();
          const baseMatches = append
            ? combined.filter((match) => {
                const key = match.matchId;
                if (!key) return true;
                if (seen.has(key)) {
                  return false;
                }
                seen.add(key);
                return true;
              })
            : combined;
          const nextMatches = baseMatches.map((match) => ({
            ...match,
            reaction: match.reaction ?? reactionMap.get(match.matchId) ?? null,
          }));
          const overrides = readMatchReactionOverrides(user?.id);
          const nextWithOverrides = applyReactionOverrides(nextMatches, overrides);
          try {
            localStorage.setItem(
              "hdg_matches_ids",
              JSON.stringify(nextWithOverrides.map((match) => match.matchId))
            );
          } catch {
            // Ignore storage failures
          }
          return nextWithOverrides;
        });
        setHasNextPage(Boolean(data.hasNextPage));
        setPage(data.page ?? pageToLoad);
        setTotalMatches(data.total ?? 0);
        hasLoadedRef.current = true;
        if (incoming.length) {
          trackMatchesEvent("matches_viewed", {
            userType: viewerAccountType ?? "unknown",
            matchScore: incoming[0]?.matchScore ?? null,
          });
        }
      } catch (err) {
        setError("Unable to load matches");
        showError("Unable to load matches. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [matchFetchOptions, showError, user?.id, viewerAccountType]
  );

  const loadAllMatches = useCallback(
    async (options?: { updateMatches?: boolean; updateLoading?: boolean }) => {
      if (!user?.id) return;
      const shouldUpdateMatches = options?.updateMatches ?? true;
      const shouldUpdateLoading = options?.updateLoading ?? shouldUpdateMatches;
      if (hasLoadedAllMatchesRef.current) {
        if (shouldUpdateMatches) {
          setMatches(allMatchesCacheRef.current);
        }
        return;
      }
      if (loadingAllMatchesRef.current) return;
      loadingAllMatchesRef.current = true;
      if (shouldUpdateLoading) {
        setLoading(true);
        setError(null);
      }
      try {
        let pageToLoad = 1;
        let hasMore = true;
        let aggregated: MatchRecord[] = [];
        while (hasMore) {
          const data = await fetchMatches(user.id, pageToLoad, matchFetchOptions);
          aggregated = aggregated.concat(data.matches ?? []);
          hasMore = Boolean(data.hasNextPage);
          pageToLoad += 1;
          if (pageToLoad > 50) {
            break;
          }
        }
        const seen = new Set<string>();
        const baseMatches = aggregated.filter((match) => {
          const key = match.matchId;
          if (!key) return true;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        let stored: string | null = null;
        try {
          stored = localStorage.getItem("hdg_matches_ids");
        } catch {
          stored = null;
        }
        setHasPreviousMatches(Boolean(stored && stored.length));
        const nextMatches = baseMatches.map((match) => ({
          ...match,
          reaction: match.reaction ?? null,
        }));
        const overrides = readMatchReactionOverrides(user?.id);
        const nextWithOverrides = applyReactionOverrides(nextMatches, overrides);
        try {
          localStorage.setItem(
            "hdg_matches_ids",
            JSON.stringify(nextWithOverrides.map((match) => match.matchId))
          );
        } catch {
          // Ignore storage failures
        }
        setAllMatchesCache(nextWithOverrides);
        if (shouldUpdateMatches) {
          setMatches(nextWithOverrides);
          setHasNextPage(false);
          setPage(1);
          setTotalMatches(baseMatches.length);
          hasLoadedRef.current = true;
        }
        hasLoadedAllMatchesRef.current = true;
      } catch {
        if (shouldUpdateLoading) {
          setError("Unable to load matches");
          showError("Unable to load matches. Please try again.");
        }
      } finally {
        if (shouldUpdateLoading) {
          setLoading(false);
        }
        loadingAllMatchesRef.current = false;
      }
    },
    [matchFetchOptions, showError, user?.id]
  );

  useEffect(() => {
    if (!isMobile && mobileFiltersOpen) {
      setMobileFiltersOpen(false);
    }
  }, [isMobile, mobileFiltersOpen]);

  const applyReactionUpdate = useCallback((profileId: string, reaction: MatchReaction) => {
    if (!profileId) return;
    setMatches((prev) =>
      prev.map((match) => {
        const candidates = getMatchCandidateIds(match);
        if (!candidates.includes(profileId)) return match;
        return { ...match, reaction };
      })
    );
    setAllMatchesCache((prev) =>
      prev.map((match) => {
        const candidates = getMatchCandidateIds(match);
        if (!candidates.includes(profileId)) return match;
        return { ...match, reaction };
      })
    );
  }, []);

  useEffect(() => {
    const handleReactionEvent = (event: Event) => {
      const detail = (event as CustomEvent<{
        profileId?: string;
        reaction?: MatchReaction;
      }>).detail;
      if (!detail?.profileId) return;
      applyReactionUpdate(detail.profileId, detail.reaction ?? null);
    };

    const handleStorage = (event: StorageEvent) => {
      const storageKey = getMatchReactionStorageKey(user?.id);
      if (!storageKey || event.key !== storageKey) return;
      if (!event.newValue) return;
      try {
        const overrides = JSON.parse(event.newValue) as MatchReactionOverrideMap;
        setMatches((prev) => applyReactionOverrides(prev, overrides));
        setAllMatchesCache((prev) => applyReactionOverrides(prev, overrides));
      } catch {
        // Ignore invalid storage payloads
      }
    };

    window.addEventListener(MATCH_REACTION_EVENT, handleReactionEvent as EventListener);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(MATCH_REACTION_EVENT, handleReactionEvent as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, [applyReactionUpdate, user?.id]);

  useEffect(() => {
    setMatches((prev) => {
      let didUpdate = false;
      const next = prev.map((match) => {
        const status = resolvePresenceStatusForMatch(
          match,
          matchPresenceRole,
          getPresenceStatus
        );
        if (status === undefined || Boolean(match.isOnline) === status) {
          return match;
        }
        didUpdate = true;
        return { ...match, isOnline: status };
      });
      return didUpdate ? next : prev;
    });

    setAllMatchesCache((prev) => {
      let didUpdate = false;
      const next = prev.map((match) => {
        const status = resolvePresenceStatusForMatch(
          match,
          matchPresenceRole,
          getPresenceStatus
        );
        if (status === undefined || Boolean(match.isOnline) === status) {
          return match;
        }
        didUpdate = true;
        return { ...match, isOnline: status };
      });
      return didUpdate ? next : prev;
    });

    setProfileCache((prev) => {
      let didUpdate = false;
      const next = { ...prev };
      Object.entries(prev).forEach(([profileId, profile]) => {
        const status = getPresenceStatus(profileId, matchPresenceRole);
        if (status === undefined || status === profile?.isOnline) {
          return;
        }
        next[profileId] = { ...profile, isOnline: status };
        didUpdate = true;
      });
      return didUpdate ? next : prev;
    });
  }, [getPresenceStatus, matchPresenceRole]);

  const presenceIds = useMemo(() => {
    const ids = matches
      .map((match) => getProfileId(match))
      .filter((value): value is string => Boolean(value));
    return Array.from(new Set(ids));
  }, [matches]);

  useEffect(() => {
    if (!presenceIds.length || !matchPresenceRole) return;
    let cancelled = false;
    const refreshPresence = async () => {
      try {
        const presence = await fetchPresenceStatuses(presenceIds, matchPresenceRole);
        if (cancelled) return;
        setMatches((prev) => {
          let didUpdate = false;
          const next = prev.map((match) => {
            const id = getProfileId(match);
            if (!id || !Object.prototype.hasOwnProperty.call(presence, id)) {
              return match;
            }
            const status = presence[id];
            if (Boolean(match.isOnline) === status) return match;
            didUpdate = true;
            return { ...match, isOnline: status };
          });
          return didUpdate ? next : prev;
        });
        setAllMatchesCache((prev) => {
          let didUpdate = false;
          const next = prev.map((match) => {
            const id = getProfileId(match);
            if (!id || !Object.prototype.hasOwnProperty.call(presence, id)) {
              return match;
            }
            const status = presence[id];
            if (Boolean(match.isOnline) === status) return match;
            didUpdate = true;
            return { ...match, isOnline: status };
          });
          return didUpdate ? next : prev;
        });
        setProfileCache((prev) => {
          let didUpdate = false;
          const next = { ...prev };
          Object.entries(presence).forEach(([profileId, status]) => {
            const existing = prev[profileId];
            if (!existing || existing.isOnline === status) return;
            next[profileId] = { ...existing, isOnline: status };
            didUpdate = true;
          });
          return didUpdate ? next : prev;
        });
      } catch {
        // Ignore polling failures to avoid interrupting the page.
      }
    };
    refreshPresence();
    const interval = window.setInterval(refreshPresence, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [matchPresenceRole, presenceIds]);

  const selectedMatch = useMemo(
    () => matches.find((match) => match.matchId === selectedMatchId) ?? null,
    [matches, selectedMatchId]
  );
  const selectedProfileId = selectedMatch ? getProfileId(selectedMatch) : null;
  const selectedBioLookupId = selectedMatch
    ? getDesignerBioLookupId(selectedMatch)
    : null;
  const selectedProfileImages = selectedBioLookupId
    ? bioImageCache[selectedBioLookupId]
    : null;
  const selectedProfileRouteId =
    selectedProfileId || selectedProfileImages?.designerId || null;
  const hasActiveMatch = lockedToActiveMatch || isAdminHomeownerPreview;
  const isMatchUnavailable = useCallback(
    (match: MatchRecord) => {
      const isOffMarket = Boolean(
        (match.profileSnapshot as Record<string, unknown> | null | undefined)?.isOffMarket
      );
      if (isOffMarket) return true;
      if (!hasActiveMatch) return false;
      if (!activeDesignerId) return false;
      return !getMatchCandidateIds(match).includes(activeDesignerId);
    },
    [activeDesignerId, hasActiveMatch]
  );
  const appliedFilter: FilterKey = useMemo(() => {
    if (sortOption === "only_loves") return "loves";
    if (sortOption === "only_likes") return "likes";
    if (sortOption === "only_not_for_me") return "not_for_me";
    if (sortOption === "online_only") return "online";
    return filter;
  }, [filter, sortOption]);
  const requiresAllMatches = appliedFilter !== "all";

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    if (requiresAllMatches) {
      loadAllMatches({ updateMatches: true, updateLoading: true });
      return;
    }
    loadMatches(1, false);
    if (!hasLoadedAllMatchesRef.current) {
      loadAllMatches({ updateMatches: false, updateLoading: false });
    }
  }, [loadAllMatches, loadMatches, requiresAllMatches, user?.id]);
  const canSelectPerfectMatch = Boolean(
    selectedMatch &&
      shouldShowSelectPerfectMatch({
        match: selectedMatch,
        isHomeowner,
        hasActiveMatch,
      })
  );

  useEffect(() => {
    if (!selectedProfileId || profileCache[selectedProfileId]) return;
    setLoadingProfileId(selectedProfileId);
    fetchMatchProfile(selectedProfileId)
      .then((profile) => {
        setProfileCache((prev) => ({ ...prev, [selectedProfileId]: profile }));
      })
      .catch(() => {
        // Ignore detail errors and rely on snapshot
      })
      .finally(() => {
        setLoadingProfileId(null);
      });
  }, [profileCache, selectedProfileId]);

  useEffect(() => {
    if (!matches.length) return;
    const profileIds = Array.from(
      new Set(
        matches
          .map((match) => getDesignerBioLookupId(match))
          .filter((value): value is string => Boolean(value))
      )
    );
    const missingIds = profileIds.filter(
      (id) => !bioImageCache[id] && !requestedBioImageIdsRef.current.has(id)
    );
    if (!missingIds.length) return;
    missingIds.forEach((id) => requestedBioImageIdsRef.current.add(id));
    let cancelled = false;
    const loadImages = async () => {
      const results = await Promise.allSettled(
        missingIds.map(async (designerId) => {
          const { data } = await apolloClient.query({
            query: GET_DESIGNER_BIO_IMAGES,
            variables: { designerId },
            fetchPolicy: "network-only",
          });
          const imageUrl = data?.getDesignerBio?.image?.url ?? null;
          const coverUrl = data?.getDesignerBio?.coverImage?.url ?? null;
          const resolvedDesignerId = data?.getDesignerBio?.designerId?._id ?? null;
          return {
            designerId,
            profilePicUrl: imageUrl ?? undefined,
            coverImageUrl: coverUrl ?? undefined,
            resolvedDesignerId: resolvedDesignerId ?? undefined,
          };
        })
      );
      if (cancelled) return;
      setBioImageCache((prev) => {
        let didUpdate = false;
        const next = { ...prev };
        results.forEach((result) => {
          if (result.status !== "fulfilled") return;
          const { designerId, profilePicUrl, coverImageUrl, resolvedDesignerId } =
            result.value;
          const nextEntry: DesignerBioImages = {};
          if (profilePicUrl) nextEntry.profilePicUrl = profilePicUrl;
          if (coverImageUrl) nextEntry.coverImageUrl = coverImageUrl;
          if (resolvedDesignerId) nextEntry.designerId = resolvedDesignerId;
          const prevEntry = next[designerId];
          const mergedEntry = { ...prevEntry, ...nextEntry };
          const prevProfile = prevEntry?.profilePicUrl;
          const prevCover = prevEntry?.coverImageUrl;
          const prevDesignerId = prevEntry?.designerId;
          if (
            prevProfile !== mergedEntry.profilePicUrl ||
            prevCover !== mergedEntry.coverImageUrl ||
            prevDesignerId !== mergedEntry.designerId
          ) {
            next[designerId] = mergedEntry;
            didUpdate = true;
          }
        });
        return didUpdate ? next : prev;
      });
    };
    loadImages();
    return () => {
      cancelled = true;
    };
  }, [apolloClient, bioImageCache, matches]);

  const counts = useMemo(() => {
    const countsSource = allMatchesCache.length ? allMatchesCache : matches;
    const all = totalMatches || countsSource.length;
    const perfect = countsSource.filter((m) => (m.matchScore ?? 0) >= 90).length;
    const loves = countsSource.filter((m) => hasLoveReaction(m.reaction ?? null)).length;
    const likes = countsSource.filter((m) => hasLikeReaction(m.reaction ?? null)).length;
    const notForMe = countsSource.filter((m) => m.reaction === "not_for_me").length;
    const online = countsSource.filter((m) => m.isOnline).length;
    return { all, perfect, loves, likes, notForMe, online };
  }, [allMatchesCache, matches, totalMatches]);

  const filteredMatches = useMemo(() => {
    const result = matches.filter((match) => {
      if (appliedFilter === "perfect") return (match.matchScore ?? 0) >= 90;
      if (appliedFilter === "loves") return hasLoveReaction(match.reaction ?? null);
      if (appliedFilter === "likes") return hasLikeReaction(match.reaction ?? null);
      if (appliedFilter === "not_for_me") return match.reaction === "not_for_me";
      if (appliedFilter === "online") return Boolean(match.isOnline);
      return true;
    });

    const sorted = [...result];
    sorted.sort((a, b) => {
      if (sortOption === "nearest") {
        return (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity);
      }
      if (sortOption === "newest") {
        return (
          new Date(b.matchCreatedAt ?? 0).getTime() -
          new Date(a.matchCreatedAt ?? 0).getTime()
        );
      }
      return (b.matchScore ?? 0) - (a.matchScore ?? 0);
    });
    return sorted;
  }, [appliedFilter, matches, sortOption]);

  const handleMobileScroll = useCallback(() => {
    const container = mobileCarouselRef.current;
    if (!container) return;
    const width = container.clientWidth;
    if (!width) return;
    const nextIndex = Math.max(
      0,
      Math.min(filteredMatches.length - 1, Math.round(container.scrollLeft / width))
    );
    setMobileIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, [filteredMatches.length]);

  useEffect(() => {
    if (!isMobile) return;
    setMobileIndex(0);
    if (mobileCarouselRef.current) {
      mobileCarouselRef.current.scrollTo({ left: 0, behavior: "auto" });
    }
  }, [appliedFilter, filteredMatches.length, isMobile, page, sortOption]);

  useEffect(() => {
    if (!filteredMatches.length) {
      setSelectedMatchId(null);
      return;
    }
    const availableMatch =
      filteredMatches.find((match) => !isMatchUnavailable(match)) ?? null;
    const selectedMatchInFilter =
      filteredMatches.find((match) => match.matchId === selectedMatchId) ?? null;
    if (!availableMatch) {
      setSelectedMatchId(null);
      return;
    }
    if (!selectedMatchInFilter || isMatchUnavailable(selectedMatchInFilter)) {
      setSelectedMatchId(availableMatch.matchId);
    }
  }, [filteredMatches, selectedMatchId, isMatchUnavailable]);

  const endOfListMessage =
    filteredMatches.length > 0 && !hasNextPage
      ? "You've explored all your curated matches for today."
      : null;

  const handleFilterChange = (next: FilterKey) => {
    setFilter(next);
    if (["only_loves", "only_likes", "only_not_for_me", "online_only"].includes(sortOption)) {
      setSortOption("best_match_rate");
      localStorage.setItem(SORT_STORAGE_KEY, "best_match_rate");
    }
    if (isMobile) {
      setMobileFiltersOpen(false);
    }
    trackMatchesEvent("filter_used", {
      userType: viewerAccountType ?? "unknown",
      matchScore: selectedMatch?.matchScore ?? null,
      filter: next,
    });
  };

  const handleSortChange = (next: SortKey) => {
    setSortOption(next);
    localStorage.setItem(SORT_STORAGE_KEY, next);
    if (isMobile) {
      setMobileFiltersOpen(false);
    }
    trackMatchesEvent("filter_used", {
      userType: viewerAccountType ?? "unknown",
      matchScore: selectedMatch?.matchScore ?? null,
      sort: next,
    });
  };

  const handleReactionChange = async (matchId: string, next: MatchReaction) => {
    if (isAdminHomeownerPreview) {
      showError("Match reactions are unavailable in admin homeowner preview.");
      return;
    }
    const targetMatch = matches.find((match) => match.matchId === matchId);
    const current = targetMatch?.reaction ?? null;
    const updated = getNextReaction(current, next);
    const profileId = targetMatch ? getProfileId(targetMatch) || targetMatch.matchId : matchId;
    const reactionProfileId = targetMatch?.profileId ?? null;

    if (navigator.vibrate) {
      navigator.vibrate(20);
    }

    setMatches((prev) =>
      prev.map((match) =>
        match.matchId === matchId ? { ...match, reaction: updated } : match
      )
    );
    setAllMatchesCache((prev) =>
      prev.map((match) =>
        match.matchId === matchId ? { ...match, reaction: updated } : match
      )
    );

    trackMatchesEvent("reaction_set", {
      userType: viewerAccountType ?? "unknown",
      matchScore: matches.find((match) => match.matchId === matchId)?.matchScore ?? null,
      reaction: updated,
    });

    try {
      await postMatchReaction(matchId, updated, user?.id ?? null, reactionProfileId);
      broadcastMatchReactionUpdate(profileId, updated, user?.id);
    } catch (err) {
      showError("Unable to save your reaction. Please try again.");
      setMatches((prev) =>
        prev.map((match) =>
          match.matchId === matchId ? { ...match, reaction: current } : match
        )
      );
      setAllMatchesCache((prev) =>
        prev.map((match) =>
          match.matchId === matchId ? { ...match, reaction: current } : match
        )
      );
    }
  };

  const handleSelectPerfectMatch = async (match: MatchRecord) => {
    if (isAdminHomeownerPreview) {
      showError("Perfect match selection is unavailable in admin homeowner preview.");
      return;
    }
    if (!user?.id) return;
    setSelectingMatchId(match.matchId);
    try {
      await postMatchReaction(match.matchId, "love", user.id, match.profileId ?? null);
      const profileId = getProfileId(match) || match.matchId;
      const selectedCandidateIds = new Set(getMatchCandidateIds(match));
      const isSameDesignerMatch = (item: MatchRecord) =>
        item.matchId === match.matchId ||
        getMatchCandidateIds(item).some((id) => selectedCandidateIds.has(id));
      setMatches((prev) =>
        prev.map((item) =>
          isSameDesignerMatch(item) ? { ...item, reaction: "love" } : item
        )
      );
      setAllMatchesCache((prev) =>
        prev.map((item) =>
          isSameDesignerMatch(item) ? { ...item, reaction: "love" } : item
        )
      );
      broadcastMatchReactionUpdate(profileId, "love", user.id);
      await selectPerfectMatch(match.matchId, user.id, match.profileId ?? null);
      setMatches((prev) =>
        prev.map((item) =>
          isSameDesignerMatch(item) ? { ...item, isMatchMade: true } : item
        )
      );
      setAllMatchesCache((prev) =>
        prev.map((item) =>
          isSameDesignerMatch(item) ? { ...item, isMatchMade: true } : item
        )
      );
      setActiveDesignerId(match.profileId ?? profileId);
      showSuccess("Perfect match selected.");
      await loadMatches(1, false);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Unable to select perfect match. Please try again.";
      showError(message);
    } finally {
      setSelectingMatchId(null);
    }
  };

  const handleSelectMatch = (match: MatchRecord) => {
    const isUnavailable = isMatchUnavailable(match);
    if (isUnavailable) return;
    setSelectedMatchId(match.matchId);
    trackMatchesEvent("profile_opened", {
      userType: viewerAccountType ?? "unknown",
      matchScore: match.matchScore ?? null,
    });
    if (isMobile) {
      const profileId = getProfileId(match);
      const fallbackLookupId = getDesignerBioLookupId(match);
      const fallbackRouteId =
        profileId ||
        (fallbackLookupId ? bioImageCache[fallbackLookupId]?.designerId : null) ||
        null;
      if (fallbackRouteId) {
        const mobileTopTags = getTopTags(match, null).slice(0, 3);
        const profileState = {
          matchScore: match.matchScore ?? null,
          locationLabel: getLocationLabel(match),
          ...(mobileTopTags.length ? { topMatchTags: mobileTopTags } : {}),
        };
        navigate(getProfileRoute(viewerAccountType, fallbackRouteId), {
          state: profileState,
        });
      } else {
        navigate(`${Pages.MATCHES}/${encodeURIComponent(match.matchId)}`);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalMatches / PAGE_SIZE));
  const pageItems = buildPaginationItems(page, totalPages);
  const showPagination =
    !requiresAllMatches && (totalPages > 1 || Boolean(endOfListMessage));

  const handlePageChange = (nextPage: number) => {
    if (nextPage === page || nextPage < 1 || nextPage > totalPages) return;
    loadMatches(nextPage, false);
  };

  const profileDetail =
    selectedProfileId && profileCache[selectedProfileId]
      ? profileCache[selectedProfileId]
      : null;

  const loadingProfile = Boolean(
    selectedProfileId && loadingProfileId === selectedProfileId
  );

  const handleViewProfile = () => {
    if (!selectedProfileRouteId) return;
    const selectedTopTags = selectedMatch
      ? getTopTags(selectedMatch, profileDetail).slice(0, 3)
      : [];
    const distanceMiles =
      selectedMatch?.distanceMiles ??
      profileDetail?.distanceMiles ??
      null;
    const profileState = {
      matchScore: selectedMatch?.matchScore ?? null,
      ...(selectedMatch ? { locationLabel: getLocationLabel(selectedMatch) } : {}),
      ...(selectedTopTags.length ? { topMatchTags: selectedTopTags } : {}),
      ...(distanceMiles !== null ? { distanceMiles } : {}),
    };
    navigate(getProfileRoute(viewerAccountType, selectedProfileRouteId), {
      state: profileState,
    });
  };

  return (
    <div className="matches-page">
      {!isMobile && <DashboardHeader title="My Matches" />}

      <MatchesToolbar
        isMobile={isMobile}
        appliedFilter={appliedFilter}
        counts={counts}
        sortOption={sortOption}
        mobileFiltersOpen={mobileFiltersOpen}
        onToggleMobileFilters={() => setMobileFiltersOpen((prev) => !prev)}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
      />

      {loading ? (
        <div className="matches-loading content-block">
          <CircularProgress size={32} />
          <span>Loading matches...</span>
        </div>
      ) : error ? (
        <div className="matches-error content-block">
          <p>We could not load your matches.</p>
          <Button variant="outlined" onClick={() => loadMatches(1, false)}>
            Retry
          </Button>
        </div>
      ) : matches.length === 0 ? (
        <div className="matches-empty content-block">
          {hasPreviousMatches && (
            <p className="matches-empty-title">You have no new matches.</p>
          )}
          <div className="matches-empty-illustration" />
          <p>
            {isAdminHomeownerPreview
              ? "This admin homeowner preview is showing eligible designers in read-only mode."
              : shouldCompleteMatchingProfile
              ? "Complete your matching profile to start receiving designer matches."
              : "No new matches at this time, haven't found the one? Try updating your matching profile to broaden your results."}
          </p>
          {!isAdminHomeownerPreview ? (
            <div className="matches-empty-actions">
              <Button
                variant="outlined"
                onClick={() => navigate(Pages.MY_MATCHING_PROFILE)}
              >
                {shouldCompleteMatchingProfile
                  ? "Complete Matching Profile"
                  : "Update Matching Profile"}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {isMobile ? (
            <div className="matches-mobile">
              <MatchesMobileCarousel
                matches={filteredMatches}
                mobileIndex={mobileIndex}
                mobileCarouselRef={mobileCarouselRef}
                onScroll={handleMobileScroll}
                onSelectMatch={handleSelectMatch}
                isHomeowner={isHomeowner}
                hasActiveMatch={hasActiveMatch}
                selectingMatchId={selectingMatchId}
                onSelectPerfectMatch={handleSelectPerfectMatch}
                isMatchUnavailable={isMatchUnavailable}
                bioImageCache={bioImageCache}
                userAccountType={viewerAccountType}
                endOfListMessage={endOfListMessage}
                hasNextPage={hasNextPage}
              />
            </div>
          ) : (
            <>
              <div className="matches-content">
                <MatchesListPanel
                  matches={filteredMatches}
                  selectedMatchId={selectedMatchId}
                  isHomeowner={isHomeowner}
                  hasActiveMatch={hasActiveMatch}
                  selectingMatchId={selectingMatchId}
                  bioImageCache={bioImageCache}
                  onSelectMatch={handleSelectMatch}
                  onReactionChange={handleReactionChange}
                  onSelectPerfectMatch={handleSelectPerfectMatch}
                  isMatchUnavailable={isMatchUnavailable}
                  disableInteractions={isAdminHomeownerPreview}
                />

                <MatchDetailPanel
                  match={selectedMatch}
                  profile={profileDetail}
                  profileImages={selectedProfileImages}
                  onViewProfile={handleViewProfile}
                  onSelectPerfectMatch={
                    selectedMatch ? () => handleSelectPerfectMatch(selectedMatch) : undefined
                  }
                  canSelectPerfectMatch={canSelectPerfectMatch}
                  selectingPerfectMatch={Boolean(
                    selectedMatch && selectingMatchId === selectedMatch.matchId
                  )}
                  loadingProfile={loadingProfile}
                  canViewProfile={
                    !activeDesignerId ||
                    Boolean(
                      selectedMatch &&
                        getMatchCandidateIds(selectedMatch).includes(activeDesignerId)
                    )
                  }
                  viewerAccountType={viewerAccountType}
                />
              </div>

              <MatchesPagination
                show={showPagination}
                page={page}
                totalPages={totalPages}
                pageItems={pageItems}
                onPageChange={handlePageChange}
                endOfListMessage={endOfListMessage}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export const MatchDetailPage = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useUserContext();
  const { effectiveRole, canAccessAdminFeatures } = useEffectiveRole();
  const { getPresenceStatus } = usePresence();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const decodedMatchId = matchId ? decodeURIComponent(matchId) : null;
  const apolloClient = useApolloClient();
  const viewerAccountType =
    effectiveRole === "Homeowner" || effectiveRole === "Designer"
      ? effectiveRole
      : user?.accountType ?? null;
  const isAdminHomeownerPreview =
    canAccessAdminFeatures &&
    effectiveRole === "Homeowner" &&
    (user?.accountType ?? "").toLowerCase() !== "homeowner";
  const matchFetchOptions = useMemo(
    () =>
      isAdminHomeownerPreview
        ? ({ previewRole: "homeowner" } as const)
        : undefined,
    [isAdminHomeownerPreview]
  );
  const matchPresenceRole =
    (viewerAccountType ?? "").toLowerCase() === "homeowner"
      ? "Designer"
      : "Homeowner";

  const [match, setMatch] = useState<MatchRecord | null>(null);
  const [profile, setProfile] = useState<MatchProfileDetail | null>(null);
  const [profileImages, setProfileImages] = useState<DesignerBioImages | null>(null);
  const [loading, setLoading] = useState(true);
  const handleMobileReactionChange = async (next: MatchReaction) => {
    if (!match) return;
    if (isAdminHomeownerPreview) {
      showError("Match reactions are unavailable in admin homeowner preview.");
      return;
    }
    const current = match.reaction ?? null;
    const updated = getNextReaction(current, next);
    const profileId = getProfileId(match) || match.matchId;

    if (navigator.vibrate) {
      navigator.vibrate(20);
    }

    setMatch({ ...match, reaction: updated });

    trackMatchesEvent("reaction_set", {
      userType: viewerAccountType ?? "unknown",
      matchScore: match.matchScore ?? null,
      reaction: updated,
    });

    try {
      await postMatchReaction(
        match.matchId,
        updated,
        user?.id ?? null,
        match.profileId ?? null
      );
      broadcastMatchReactionUpdate(profileId, updated, user?.id);
    } catch {
      showError("Unable to save your reaction. Please try again.");
      setMatch({ ...match, reaction: current });
    }
  };

  useEffect(() => {
    if (!decodedMatchId) {
      setLoading(false);
      return;
    }
    if (!user?.id) return;
    const loadDetail = async () => {
      try {
        setLoading(true);
        setMatch(null);
        setProfile(null);
        const data = await fetchMatches(user.id, 1, matchFetchOptions);
        const found =
          data.matches.find((item) => item.matchId === decodedMatchId) ?? null;
        setMatch(found);
        setProfileImages(null);
        if (found) {
          trackMatchesEvent("profile_opened", {
            userType: viewerAccountType ?? "unknown",
            matchScore: found.matchScore ?? null,
          });
        }
        const profileId = found ? getProfileId(found) : null;
        if (profileId) {
          const detail = await fetchMatchProfile(profileId);
          setProfile(detail);
        }
        const bioLookupId = found ? getDesignerBioLookupId(found) : null;
        if (bioLookupId) {
          try {
            const { data } = await apolloClient.query({
              query: GET_DESIGNER_BIO_IMAGES,
              variables: { designerId: bioLookupId },
              fetchPolicy: "network-only",
            });
            const imageUrl = data?.getDesignerBio?.image?.url ?? null;
            const coverUrl = data?.getDesignerBio?.coverImage?.url ?? null;
            const resolvedDesignerId = data?.getDesignerBio?.designerId?._id ?? null;
            setProfileImages({
              profilePicUrl: imageUrl ?? undefined,
              coverImageUrl: coverUrl ?? undefined,
              designerId: resolvedDesignerId ?? undefined,
            });
          } catch {
            // Ignore image errors and rely on profile snapshot
          }
        }
      } catch {
        showError("Unable to load match detail.");
      } finally {
        setLoading(false);
      }
    };
    loadDetail();
  }, [apolloClient, decodedMatchId, matchFetchOptions, showError, user?.id, viewerAccountType]);

  useEffect(() => {
    if (!match) return;
    const status = resolvePresenceStatusForMatch(
      match,
      matchPresenceRole,
      getPresenceStatus
    );
    if (status !== undefined) {
      setMatch((prev) => {
        if (!prev || Boolean(prev.isOnline) === status) return prev;
        return { ...prev, isOnline: status };
      });
    }

    const profileId = getProfileId(match);
    const profileStatus = profileId
      ? getPresenceStatus(profileId, matchPresenceRole)
      : undefined;
    if (profileStatus !== undefined) {
      setProfile((prev) => {
        if (!prev || prev.isOnline === profileStatus) return prev;
        return { ...prev, isOnline: profileStatus };
      });
    }
  }, [getPresenceStatus, match, matchPresenceRole]);


  useEffect(() => {
    if (!isMobile) return;
    document.body.classList.add("matches-mobile-detail");
    return () => {
      document.body.classList.remove("matches-mobile-detail");
    };
  }, [isMobile]);

  const handleViewProfile = () => {
    const profileId = match ? getProfileId(match) : null;
    const routeId = profileId || profileImages?.designerId || null;
    if (!routeId) return;
    const selectedTopTags = match ? getTopTags(match, profile).slice(0, 3) : [];
    const distanceMiles = match?.distanceMiles ?? profile?.distanceMiles ?? null;
    const profileState = {
      matchScore: match?.matchScore ?? null,
      ...(match ? { locationLabel: getLocationLabel(match) } : {}),
      ...(selectedTopTags.length ? { topMatchTags: selectedTopTags } : {}),
      ...(distanceMiles !== null ? { distanceMiles } : {}),
    };
    navigate(getProfileRoute(viewerAccountType, routeId), {
      state: profileState,
    });
  };

  if (loading) {
    return (
      <div className="matches-page">
        <div className="matches-loading content-block">
          <CircularProgress size={32} />
          <span>Loading match...</span>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="matches-page matches-detail-page">
        <div className="matches-empty content-block">
          <p>Match not found.</p>
          <div className="matches-empty-actions">
            <Button variant="outlined" onClick={() => navigate(Pages.MATCHES)}>
              Back to Matches
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const profileId = match ? getProfileId(match) : null;
  const routeId = profileId || profileImages?.designerId || null;
  const isHomeownerViewer = (viewerAccountType ?? "").toLowerCase() !== "designer";
  const showLearnMore = Boolean(routeId && isHomeownerViewer);

  const handleLearnMore = () => {
    if (!routeId || !isHomeownerViewer) return;
    navigate(Pages.DESIGNER_COMPATIBILITY_DETAILS.replace(":designerId", routeId));
  };

  if (isMobile) {
    return (
      <MatchMobileDetail
        match={match}
        profile={profile}
        profileImages={profileImages}
        userAccountType={viewerAccountType}
        onClose={() => navigate(Pages.MATCHES)}
        onViewProfile={handleViewProfile}
        onLearnMore={handleLearnMore}
        showLearnMore={showLearnMore}
        showViewProfile={Boolean(routeId)}
        onReactionChange={handleMobileReactionChange}
        disableInteractions={isAdminHomeownerPreview}
      />
    );
  }

  return (
    <div className="matches-page matches-detail-page">
      <Button variant="outlined" className="matches-back" onClick={() => navigate(Pages.MATCHES)}>
        Back to Matches
      </Button>
      <MatchDetailPanel
        match={match}
        profile={profile}
        profileImages={profileImages}
        onViewProfile={handleViewProfile}
        loadingProfile={false}
        viewerAccountType={viewerAccountType}
      />
    </div>
  );
};
